'use client';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { priceInfo } from '@/utils/price';
import { useContext } from 'react';
import usePageTrack from '../hooks/usePageTrack';
import CollectionItemMB from './CollectionItemMB';
import CollectionItemPcB, { IProduct } from './CollectionItemPcB';

interface IProps {
  collection_list: {
    image: string;
    intro_image: string;
    intro_title: string;
    intro_description: string;
    bg_color: string;
  };
  product_list: Array<[IProduct, IProduct]>;
  exchangeRateList: Array<{ name?: string; exchange_rate?: string }>;
}
export default function CollectionList({
  collection_list,
  product_list,
  exchangeRateList,
}: IProps) {
  const { userInfo, currency } = useStore();
  const { deviceType } = useContext(ResponsiveContext);
  const { trackProduct, trackPageMktClick } = usePageTrack({});
  const isWeb = deviceType === EPlatform.WEB;
  const exchangeRate =
    exchangeRateList.find(
      (item) => item.name === (currency.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY),
    )?.exchange_rate || '1';
  const productListWrapperClass = isWeb
    ? 'grid grid-cols-3 px-[48px] gap-y-[72px] gap-x-[16px]'
    : 'flex flex-col px-[8px] gap-[8px]';
  // * 商品曝光/点击埋点
  const reportProductExp = (product: IProduct, index: number, type: 'exp' | 'click' = 'exp') => {
    const { short_name, id, sku, shop_sku, business_sku } = product;
    const { price, sell_price } = priceInfo(userInfo, product, currency);
    trackProduct(type, {
      module_name: '商品介绍',
      pits_index: index,
      product_name: short_name,
      product_id: String(id),
      product_sku: sku,
      business_sku,
      shop_sku,
      dollar_exchange_rate: exchangeRate,
      currency: currency.name,
      product_price: Number(price),
      base_current_product_price: Number(price),
      current_product_price: Number(sell_price),
    });
  };
  const CollectionItemComp = isWeb ? CollectionItemPcB : CollectionItemMB;
  return (
    <div
      className={`w-full bg-[${collection_list.bg_color}] ${isWeb ? 'pt-[88px] pb-[80px]' : ''}`}
    >
      {isWeb ? (
        <Image
          src={collection_list.image}
          alt="ZEELOOL x PRABAL GURUNG"
          width={873}
          height={47}
          className="mx-auto mb-[88px]"
        />
      ) : (
        <div
          className="p-[20px_14px] flex justify-between items-center "
          onClick={() => trackPageMktClick({ module_name: 'PG介绍' })}
        >
          <div className="w-[205px]">
            <div className="text-[16px]/[20px] mb-[12px] font-be">
              {collection_list.intro_title}
            </div>
            <div className="text-[12px]/[18px] w-[205px]">{collection_list.intro_description}</div>
          </div>
          <Image
            src={collection_list.intro_image}
            alt="ZEELOOL x PRABAL GURUNG"
            width={128}
            height={100}
          />
        </div>
      )}
      <div className={`${productListWrapperClass} `}>
        {isWeb && (
          <div className="relative" onClick={() => trackPageMktClick({ module_name: 'PG介绍' })}>
            <Image
              src={collection_list.intro_image}
              alt="Prabal Gurung"
              width={597}
              height={759}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-[24px] px-[24px] w-full text-white">
              <div className="text-[48px]/[58px] mb-[16px] font-be">
                {collection_list.intro_title}
              </div>
              <div className="text-[20px]/[24px] font-br">{collection_list.intro_description}</div>
            </div>
          </div>
        )}
        {product_list.map((item, index) => (
          <CollectionItemComp
            key={index}
            data={item}
            index={index}
            isReverse={index % 2 !== 0}
            reportProductExp={reportProductExp}
          />
        ))}
      </div>
    </div>
  );
}
